import { getCookie } from '../../GlobalComponents/GlobalFunctions'
import { getAPIURL } from '../../GlobalComponents/URLs'
import { browserCache } from '../meta/metaAPI'
import { FetchVendorNameReturnPayload, FetchVendorNamesReturnPayload } from '../order/orderAPI'

export interface FetchPartInfoParams {
    PartKey?: number
    PartNumber?: string
    SearchMfgPartNumberFallback: boolean
}
// todo make inventory ds a typescript file
export interface FetchPartInfoReturnPayload {
    ProductLifeCycleKey: number
    Returnable: ' ' | 'Y' | 'N' | '?'
    MfgName: string
    MfgPartNumber: string
    MarketingCode: string
    InventoryTypeKey: number
    VendorID: string
    Description: string
    ReturnableLastUpdatedDate: Date
    PartNumber: string
    Partkey: number
    // Available: number
    BoxQuantity: string
    LeadTime: number
    ReplacedByMfgPartnumber?: string
    Constrained: boolean
    ProductionNoticeDefinition: string
    SellMinimum: number
    SellMultiple: number
}

export const fetchPartInfo = async (
    payload: FetchPartInfoParams
): Promise<FetchPartInfoReturnPayload | null> => {
    let url = getAPIURL() + 'inventory/parts'
    // SelectColumns are hard-coded on the API side for security when in customer mode
    let SelectColumns = [
        'Returnable',
        'ProductLifeCycleKey',
        'MfgName',
        'MfgPartNumber',
        'MarketingCode',
        'InventoryTypeKey',
        'VendorID',
        'Description',
        'ReturnableLastUpdatedDate',
        // 'Available',
        'BoxQuantity',
        'PartNumber',
        'Partkey',
        // 'SymbolNumber',
        'LeadTime',
        'ReplacedByMfgPartnumber',
        'Constrained',
        'ProductionNoticeDefinition',
        'SellMinimum',
        'SellMultiple',
    ]
    if (!payload.PartNumber && !payload.PartKey) throw new Error('must have partkey or partnumber')
    let req = await fetch(url, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, SelectColumns }),
    })
    if (req.status === 204) {
        return null
    }
    let json: FetchPartInfoReturnPayload = await req.json()

    return json
}

export interface FetchAdvancedPartParams {
    SearchTerm: string
    CustomerAccountCode: string
    Manufacturers: string[]
    VendorIds: string[]
    Description: string
}

export interface FetchAdvancedPartReturnPayload {
    InventoryCount: number
    Inventory: AdvancedPart[]
    MfgNames: string[]
    VendorIds: string[]
}

export interface AdvancedPart {
    Partkey: number
    PartNumber: string
    Manufacturer: string
    MfgPartNumber: string
    VendorCode: string
    Description: string
    Available: number
}

export const fetchAdvancedPart = async (
    payload: FetchAdvancedPartParams,
    controller: AbortController
) => {
    let url = getAPIURL() + 'inventory/search/advanced'

    let req = await fetch(url, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
    })
        .then((resp) => {
            if (resp.ok) {
                return resp.json()
            } else throw new Error('bad request ' + resp.statusText)
        })
        .catch((err) => {
            console.error('Error fetching advanced part')
        })

    let json: FetchAdvancedPartReturnPayload = await req

    return json
}

export type FetchProductLifeCyclesReturnPayload = Array<{
    ProductLifeCycleKey: number
    Description: string
}>

export const fetchProductLifeCycles = async (): Promise<FetchProductLifeCyclesReturnPayload> => {
    let url = getAPIURL() + 'inventory/ProductLifecycles'

    let req = await fetch(url, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
    })
    let json: FetchProductLifeCyclesReturnPayload = await req.json()
    return json
}

export const productLifeCycles = {
    get table() {
        // load when needed
        let prom = fetchProductLifeCycles().then((data) => {
            Object.defineProperty(this, 'table', {
                value: data,
                writable: false,
                configurable: false,
                enumerable: false,
            })
        })
        // return so still awaitable
        return prom
    },
}

export type Vendor = {
    VendorID: string
    VendorName: string
    VendorIDName: string
}

export type Vendors = Array<Vendor>

export const fetchVendorData = async (): Promise<Vendors> => {
    const cacheKey = `vendorNames${getCookie('CoRoot')}`
    let vendorNames: Vendors = browserCache.getItem(cacheKey)
    if (!vendorNames) {
        let url = `${getAPIURL()}inventory/vendors`

        let req = await fetch(url, {
            credentials: 'include',
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ IncludeInactive: false }),
        })
        let json: FetchVendorNamesReturnPayload[] = await req.json()

        vendorNames = json.map((v) => {
            return { VendorID: v.VENDID, VendorName: v.NAME, VendorIDName: v.VendorIDName }
        })
        browserCache.setItem(cacheKey, vendorNames, 3600000)
    }

    return vendorNames
}

export type VendorProductCodes = Array<{
    VendorID: string
    ProductCode: string
    VendorIDProductCode: string
    ProductCodeName: string
    VendorIDProductCodeCount: number
}>

export const fetchProductCodeData = async (
    selectedVendors: Vendors
): Promise<VendorProductCodes> => {
    let url = `${getAPIURL()}inventory/vendorproductcodes`

    let req = await fetch(url, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ VendorFilter: selectedVendors, IncludeInactive: false }),
    })
    let json: VendorProductCodes = await req.json()
    return json
}
